import type { MdCaseSeverity } from 'features/mdCases/mdCase'
import {
  GeckoAlert,
  type RolodexEntryJson,
  type RolodexEntryPatchJson,
  type RolodexEntryPutJson,
  RolodexEntryType,
  type RolodexLoadedEntryJson,
} from 'types'
import type uPlot from 'uplot'

export const MANUAL_ALERT_SOURCE_TYPE = 'manual'

export type PlotBounds = {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

type BuildManualAlertEntryArgs = {
  id: string
  title?: string | null
  summary: string
  severity?: MdCaseSeverity | null
  /** Source chart the alert was drafted from. Pass `null` for ad-hoc (transient) views. */
  chartID: string | null
  siteID: string
  organizationID: string
  primaryDatapointID: string
  datapointIDs: readonly string[]
  renderStartAt: string
  renderEndAt: string
  focusStartAt: string
  focusEndAt: string
  caseID?: string | null
  createdAt?: string | null
  createdBy?: string | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const getRolodexEntryJson = (value: unknown): RolodexEntryJson | null => {
  if (!isRecord(value)) {
    return null
  }

  const candidateEntry = isRecord(value.entry)
    ? (value as RolodexLoadedEntryJson).entry
    : value

  return isRecord(candidateEntry) &&
    typeof candidateEntry.id === 'string' &&
    typeof candidateEntry.type === 'string' &&
    isRecord(candidateEntry.data) &&
    isRecord(candidateEntry.tags)
    ? (candidateEntry as RolodexEntryJson)
    : null
}

export const parseProcessDataAlert = (entry: unknown): GeckoAlert | null => {
  const rolodexEntry = getRolodexEntryJson(entry)
  if (!rolodexEntry) {
    return null
  }

  try {
    const geckoAlert = new GeckoAlert(rolodexEntry)
    return isProcessDataCapableAlert(geckoAlert) ? geckoAlert : null
  } catch {
    return null
  }
}

const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []

export const isProcessDataCapableAlert = (alert: GeckoAlert): boolean =>
  alert.data.source_type === MANUAL_ALERT_SOURCE_TYPE ||
  Boolean(
    alert.data.datapoint_ids?.length &&
    alert.data.render_start_at &&
    alert.data.render_end_at &&
    alert.data.focus_start_at &&
    alert.data.focus_end_at
  )

export const normalizeAlertDatapointIDs = (
  primaryDatapointID: string,
  datapointIDs: readonly string[]
): string[] => {
  const normalized: string[] = []
  const seen = new Set<string>()

  ;[primaryDatapointID, ...datapointIDs].forEach((id) => {
    const trimmedID = id.trim()
    if (!trimmedID || seen.has(trimmedID)) {
      return
    }

    seen.add(trimmedID)
    normalized.push(trimmedID)
  })

  return normalized
}

export const buildManualAlertEntry = ({
  id,
  title,
  summary,
  severity,
  chartID,
  siteID,
  organizationID,
  primaryDatapointID,
  datapointIDs,
  renderStartAt,
  renderEndAt,
  focusStartAt,
  focusEndAt,
  caseID = null,
  createdAt = null,
  createdBy = null,
}: BuildManualAlertEntryArgs): RolodexEntryPutJson => {
  const normalizedPrimaryDatapointID = primaryDatapointID.trim()
  const normalizedDatapointIDs = normalizeAlertDatapointIDs(
    normalizedPrimaryDatapointID,
    datapointIDs
  )

  if (!normalizedPrimaryDatapointID || normalizedDatapointIDs.length === 0) {
    throw new Error('Manual alerts require at least one datapoint ID')
  }

  return {
    id,
    type: RolodexEntryType.ALERT,
    tags: {
      site: siteID,
      organization: organizationID,
      ...(chartID ? { chart: chartID } : {}),
      datapoints: normalizedPrimaryDatapointID,
    },
    data: {
      source_type: MANUAL_ALERT_SOURCE_TYPE,
      priority: severity === 'High' ? 1 : severity === 'Low' ? 3 : 2,
      timestamp: focusStartAt,
      acknowledged: false,
      status: 'Alarm',
      alerted_value: 0,
      setpoint: 0,
      title: title?.trim() || null,
      summary: summary.trim(),
      severity: severity ?? null,
      chart_id: chartID,
      site_id: siteID,
      organization_id: organizationID,
      primary_datapoint_id: normalizedPrimaryDatapointID,
      datapoint_ids: normalizedDatapointIDs,
      render_start_at: renderStartAt,
      render_end_at: renderEndAt,
      focus_start_at: focusStartAt,
      focus_end_at: focusEndAt,
      case_id: caseID,
      created_at: createdAt,
      created_by: createdBy,
    },
  }
}

export const parseManualAlerts = (entries: readonly unknown[]): GeckoAlert[] =>
  entries.flatMap((entry) => {
    const alert = parseProcessDataAlert(entry)
    return alert ? [alert] : []
  })

const alertOverlapsTimeRange = (
  alert: GeckoAlert,
  timeRange: { from: string; to: string }
) =>
  new Date(alert.data.render_end_at ?? '').getTime() >=
    new Date(timeRange.from).getTime() &&
  new Date(alert.data.render_start_at ?? '').getTime() <=
    new Date(timeRange.to).getTime()

const alertMatchesSensors = (
  alert: GeckoAlert,
  sensorIDs: readonly string[]
) => {
  if (sensorIDs.length === 0) {
    return false
  }

  const sensorIDSet = new Set(sensorIDs)
  return (alert.data.datapoint_ids ?? []).some((sensorID) =>
    sensorIDSet.has(sensorID)
  )
}

export const selectManualAlertsForSensors = ({
  alerts,
  sensorIDs,
}: {
  alerts: readonly GeckoAlert[]
  sensorIDs: readonly string[]
}) =>
  alerts.filter(
    (alert) =>
      isProcessDataCapableAlert(alert) && alertMatchesSensors(alert, sensorIDs)
  )

export const selectVisibleManualAlerts = ({
  alerts,
  timeRange,
  sensorIDs,
}: {
  alerts: readonly GeckoAlert[]
  timeRange: { from: string; to: string }
  sensorIDs: readonly string[]
}) =>
  selectManualAlertsForSensors({ alerts, sensorIDs }).filter((alert) =>
    alertOverlapsTimeRange(alert, timeRange)
  )

export const buildAlertBadgeLanes = (
  alerts: readonly {
    id: string
    startAt: string
    endAt: string
  }[]
) => {
  const laneEndTimes: number[] = []
  const alertsInOrder = [...alerts].sort(
    (left, right) =>
      new Date(left.startAt).getTime() - new Date(right.startAt).getTime()
  )

  return alertsInOrder.map((alert) => {
    const alertStart = new Date(alert.startAt).getTime()
    const alertEnd = new Date(alert.endAt).getTime()
    let lane = laneEndTimes.findIndex((laneEnd) => laneEnd < alertStart)

    if (lane === -1) {
      lane = laneEndTimes.length
      laneEndTimes.push(alertEnd)
    } else {
      laneEndTimes[lane] = alertEnd
    }

    return {
      alertId: alert.id,
      lane,
    }
  })
}

export const getPlotBounds = (
  uPlotInstance: uPlot | null,
  chartHost: HTMLDivElement | null
): PlotBounds | null => {
  if (!uPlotInstance || !chartHost) {
    return null
  }

  const hostRect = chartHost.getBoundingClientRect()
  const plotRect = uPlotInstance.over.getBoundingClientRect()

  return {
    left: plotRect.left - hostRect.left,
    top: plotRect.top - hostRect.top,
    right: plotRect.right - hostRect.left,
    bottom: plotRect.bottom - hostRect.top,
    width: plotRect.width,
    height: plotRect.height,
  }
}

export const buildCaseAlertRemovalPatch = (
  rawCase: Record<string, unknown>,
  alertId: string
): RolodexEntryPatchJson | null => {
  const caseId = typeof rawCase.id === 'string' ? rawCase.id : null
  if (!caseId || !isRecord(rawCase.data)) {
    return null
  }

  const rawAlerts = isRecord(rawCase.data.alerts) ? rawCase.data.alerts : {}
  const nextAlertIds = normalizeStringArray(rawAlerts.alert_ids).filter(
    (currentAlertId) => currentAlertId !== alertId
  )
  const nextAlertItems = Array.isArray(rawAlerts.items)
    ? rawAlerts.items.filter((item) => !isRecord(item) || item.id !== alertId)
    : []

  return {
    id: caseId,
    type: RolodexEntryType.CASE,
    data: {
      alerts: {
        alert_ids: nextAlertIds,
        items: nextAlertItems,
      },
    },
  }
}
